import { Task } from '@/types/tasks';
import { createDragStartHandler, createDragOverHandler, createDragEndHandler } from './drag-handlers';
import { useTaskStateUpdates } from './use-task-state-updates';
import { useAuth } from '@/contexts/auth';
import { useCallback, useMemo, useEffect, useState } from 'react';

interface TaskManagerOperations {
  updateTask: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  updateTaskOrder?: (taskId: string, newOrder: number) => void;
  setLocalState?: (useLocal: boolean) => void;
  setLocalActiveTasks?: (tasks: Task[]) => void;
  setLocalCompletedTasks?: (tasks: Task[]) => void;
}

interface TaskManagerBoardDragProps {
  activeTasks: Task[];
  completedTasks: Task[];
  taskManagerOperations: TaskManagerOperations;
}

/**
 * Hook that provides drag-and-drop functionality specifically for TaskManager page
 * Now integrates with TaskManager's local state system like workbook/storage systems
 */
export const useTaskManagerBoardDrag = (
  setActiveTask: (task: Task | null) => void,
  setShouldUseLocalTasks: (value: boolean) => void,
  { activeTasks, completedTasks, taskManagerOperations }: TaskManagerBoardDragProps
) => {
  const { user } = useAuth();
  const { isUpdating } = useTaskStateUpdates();
  const [isDragUpdating, setIsDragUpdating] = useState(false);
  
  // Create unified database update function that matches our new API
  const updateTaskInDatabase = useCallback(async (taskId: string, updates: Partial<Task>) => {
    console.log('[TaskManagerBoardDrag] Database update:', { taskId, updates });
    // Use TaskManager's enhanced updateTask which handles both local state and database
    taskManagerOperations.updateTask(taskId, updates, true); // silent=true for drag operations
  }, [taskManagerOperations]);
  
  // Memoize drag handlers to prevent unnecessary re-renders
  const handleDragStart = useCallback((event: any) => {
    // Switch TaskManager to use local state during drag operations
    if (taskManagerOperations.setLocalState) {
      taskManagerOperations.setLocalState(true);
    }
    
    // Use the factory-created handler with current task arrays
    createDragStartHandler(
      setActiveTask,
      activeTasks,
      completedTasks
    )(event);
  }, [setActiveTask, activeTasks, completedTasks, taskManagerOperations]);
  
  // Create memoized drag over handler with current task arrays
  const handleDragOver = useMemo(() => 
    createDragOverHandler(activeTasks, completedTasks),
    [activeTasks, completedTasks]
  );
  
  const handleDragEnd = useCallback(async (event: any) => {
    setIsDragUpdating(true);
    
    try {
      // Use the factory-created handler with TaskManager's enhanced state setters
      await createDragEndHandler(
        setActiveTask,
        activeTasks,
        completedTasks,
        taskManagerOperations.setLocalActiveTasks || (() => {}),
        taskManagerOperations.setLocalCompletedTasks || (() => {}),
        updateTaskInDatabase
      )(event);
    } finally {
      setIsDragUpdating(false);
      
      // Reset TaskManager to use React Query data after drag completes
      setTimeout(() => {
        if (taskManagerOperations.setLocalState) {
          taskManagerOperations.setLocalState(false);
        }
      }, 500);
    }
  }, [
    setActiveTask,
    activeTasks,
    completedTasks,
    taskManagerOperations,
    updateTaskInDatabase
  ]);

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    isUpdating: isUpdating || isDragUpdating
  };
}; 